import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class AnalyticsErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    this.props.onError?.(error, errorInfo);
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center rounded-lg border border-[var(--anl-border)] bg-[var(--anl-surface)] p-8">
          <AlertTriangle className="mb-4 h-12 w-12" style={{ color: 'var(--anl-error)' }} />
          <h3 className="mb-2 text-lg font-semibold text-[var(--anl-text)]">Analytics Error</h3>
          <p className="mb-4 max-w-md text-center text-sm text-[var(--anl-text-muted)]">
            Something went wrong while loading the analytics data. This might be due to a network
            issue or data processing error.
          </p>

          <div className="flex space-x-3">
            <button
              onClick={this.handleRetry}
              className="flex items-center space-x-2 rounded-md bg-[var(--anl-error)] px-4 py-2 text-white transition-colors hover:opacity-90"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Try Again</span>
            </button>

            <button
              onClick={() => window.location.reload()}
              className="rounded-md border border-[var(--anl-border)] px-4 py-2 text-[var(--anl-text)] transition-colors hover:bg-[var(--anl-surface-elev)]"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export function withAnalyticsErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode,
) {
  return function WrappedComponent(props: P) {
    return (
      <AnalyticsErrorBoundary fallback={fallback}>
        <Component {...props} />
      </AnalyticsErrorBoundary>
    );
  };
}
